import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useRouterState } from '@tanstack/react-router';
import { SidebarNav } from './Sidebar.js';

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Mobile slide-over navigation (lg:hidden). Mirrors SendModalShell's portal +
 * backdrop + Esc + body-scroll-lock pattern, and additionally closes on route
 * change so navigating from inside the drawer dismisses it. The nav body is the
 * shared SidebarNav, so desktop and mobile stay in sync.
 */
export function MobileNavDrawer({ open, onClose }: Props) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Close on route change.
  useEffect(() => {
    if (open) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Close on Esc.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;
  const root = typeof document !== 'undefined' ? document.body : null;
  if (!root) return null;

  return createPortal(
    <div
      className="lg:hidden fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Navigation"
    >
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(13, 43, 74, 0.55)' }}
        onClick={onClose}
      />
      <div className="absolute inset-y-0 left-0 w-72 max-w-[80vw] bg-cream shadow-xl overflow-y-auto">
        <SidebarNav onNavigate={onClose} />
      </div>
    </div>,
    root,
  );
}
